import { useCallback, useEffect, useRef, useState } from 'react'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'

import { useCampaignSelection } from '@/entities/campaign'
import { useCampaignDates, useDashboardMetrics } from '@/entities/dailyMetric'
import { CampaignSelector } from '@/components/features/CampaignSelector'
import { ChatDashboard } from '@/pages/dashboard-chat/ChatDashboard'

import {
  CampaignMetricsTable,
  ChartsBlock,
  CommonMetricsTable,
  CostMetricsTable,
  DateRangePicker,
  DialerMetricsTable,
  DispositionTable,
  DurationsTable,
  ExportCSVButton,
  PerformanceSummaryCard,
  SectionHeader,
} from './ui'

const formatStr = 'yyyy-MM-dd'

// Parse URL hash to get initial date range
function parseHashParams(): { startDate?: string; endDate?: string } {
  const hash = window.location.hash.slice(1)
  if (!hash) return {}
  const params = new URLSearchParams(hash)
  return {
    startDate: params.get('start') || undefined,
    endDate: params.get('end') || undefined,
  }
}

// Update URL hash with current date range
function updateHashParams(startDate?: string, endDate?: string) {
  const params = new URLSearchParams()
  if (startDate) params.set('start', startDate)
  if (endDate) params.set('end', endDate)
  const hash = params.toString()
  window.history.replaceState(null, '', hash ? `#${hash}` : window.location.pathname)
}

export function Dashboard() {
  const { selectedCampaign } = useCampaignSelection()
  if (selectedCampaign?.telephony === 'custom') {
    return <ChatDashboard />
  }
  return <VoiceDashboard />
}

function VoiceDashboard() {
  // Campaign state (persisted to session storage)
  const { campaignId: selectedCampaignId, setCampaignId: setSelectedCampaignId, campaigns, campaignsLoading } = useCampaignSelection()

  // Parse initial values from URL hash
  const initialHash = useRef(parseHashParams())

  // State for date range
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const { startDate, endDate } = initialHash.current
    if (startDate && endDate) {
      return {
        from: new Date(startDate + 'T00:00:00'),
        to: new Date(endDate + 'T00:00:00'),
      }
    }
    return undefined
  })

  // Track if we've done the initial submission
  const hasSubmittedInitialRef = useRef(!!initialHash.current.startDate)

  // Fetch default date range when campaign changes
  const { data: campaignDates, isLoading: datesLoading } = useCampaignDates(selectedCampaignId)

  // Fetch dashboard metrics when we have campaign and date range
  const startDateStr = dateRange?.from ? format(dateRange.from, formatStr) : null
  const endDateStr = dateRange?.to ? format(dateRange.to, formatStr) : null

  const {
    data: metricsData,
    isLoading: metricsLoading,
    isFetching,
  } = useDashboardMetrics(selectedCampaignId, startDateStr, endDateStr)

  // Update URL hash when date range changes
  useEffect(() => {
    updateHashParams(startDateStr || undefined, endDateStr || undefined)
  }, [startDateStr, endDateStr])

  // Update date range when campaign dates are fetched (only if no hash params)
  useEffect(() => {
    if (campaignDates && !hasSubmittedInitialRef.current) {
      const from = new Date(campaignDates.startDate + 'T00:00:00')
      const to = new Date(campaignDates.endDate + 'T00:00:00')
      setDateRange({ from, to })
      hasSubmittedInitialRef.current = true
    }
  }, [campaignDates])

  // Reset initial submission flag when campaign changes
  useEffect(() => {
    // Only reset if we don't have hash params
    if (!initialHash.current.startDate) {
      hasSubmittedInitialRef.current = false
    }
    // Clear the initial hash after first use
    initialHash.current = {}
  }, [selectedCampaignId])

  // Handle campaign selection change — keep current date range visible during transition
  const handleCampaignChange = useCallback((campaignId: number | null) => {
    setSelectedCampaignId(campaignId)
    hasSubmittedInitialRef.current = false
  }, [setSelectedCampaignId])

  // Handle date range change
  const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
    if (!range) return
    setDateRange(range)
  }, [])

  // Get selected campaign info
  const selectedCampaign = metricsData?.campaign || null

  const initialLoading = (campaignsLoading || datesLoading || metricsLoading) && !metricsData

  return (
    <div className="w-full h-full p-6 overflow-y-auto">
      {/* Campaign Selector at the top */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex-shrink-0">
          <h1 className="text-heading font-bold mb-1">Daily Metrics</h1>
          <p className="text-body text-muted-foreground">Monitor your daily call performance metrics</p>
        </div>
        <CampaignSelector
          campaigns={campaigns}
          selectedCampaignId={selectedCampaignId}
          onCampaignChange={handleCampaignChange}
          className="w-[400px]"
          disabled={campaignsLoading}
        />
      </div>

      {/* Date Range Picker */}
      <div className="flex items-center gap-2">
        <DateRangePicker dateRange={dateRange} onDateRangeChange={handleDateRangeChange} />

        {isFetching && !initialLoading && (
          <div className="flex items-center gap-2 text-body text-muted-foreground">
            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
            Loading...
          </div>
        )}

        <div className="ml-auto">
          {metricsData?.metricsData && metricsData.metricsData.length > 0 && (
            <ExportCSVButton metricsData={metricsData.metricsData} />
          )}
        </div>
      </div>

      {/* Show loading state or content */}
      {initialLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-body text-muted-foreground">Loading dashboard data...</div>
        </div>
      ) : !selectedCampaignId ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-body text-muted-foreground">Select a campaign to view metrics</div>
        </div>
      ) : (
        <div className={isFetching ? 'opacity-60 transition-opacity duration-200' : 'transition-opacity duration-200'}>
          {/* Performance Summary Card */}
          {metricsData?.metricsData && metricsData.metricsData.length > 0 ? (
            <PerformanceSummaryCard metricsData={metricsData.metricsData} startDate={startDateStr} endDate={endDateStr} />
          ) : metricsData ? (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border text-center text-muted-foreground text-body">
              No performance data for the selected date range
            </div>
          ) : null}

          {/* Performance Overview Section */}
          {metricsData?.metricsData && metricsData.metricsData.length > 0 && (
            <div className="mb-8">
              <SectionHeader title="Performance Overview" />
              <ChartsBlock
                metricsData={metricsData.metricsData}
                durationMetrics={metricsData.durationMetrics}
              />
            </div>
          )}

          {/* Detailed Metrics Section */}
          {metricsData?.metricsData && metricsData.metricsData.length > 0 && (
            <div className="mb-6">
              <SectionHeader title="Detailed Metrics" />
              <CommonMetricsTable metricsData={metricsData.metricsData} />
            </div>
          )}

          {/* Disposition Metrics */}
          {metricsData?.dispositionMetrics && metricsData.dispositionMetrics.length > 0 ? (
            <DispositionTable
              dispositionMetrics={metricsData.dispositionMetrics}
              metricsData={metricsData.metricsData}
              selectedCampaign={selectedCampaign}
            />
          ) : metricsData?.metricsData && metricsData.metricsData.length > 0 ? (
            <div className="mb-6 bg-white rounded-lg shadow-sm p-6 border border text-center text-muted-foreground text-body">
              No disposition data for this period
            </div>
          ) : null}

          {/* Dialer Report for Asterisk campaigns */}
          {selectedCampaign?.telephony === 'asterisk' && metricsData?.metricsData && (
            <DialerMetricsTable
              metricsData={metricsData.metricsData}
              dispositionMetrics={metricsData.dispositionMetrics}
              selectedCampaign={selectedCampaign}
            />
          )}

          {/* Duration Metrics */}
          {metricsData?.durationMetrics && metricsData.durationMetrics.length > 0 ? (
            <DurationsTable durationMetrics={metricsData.durationMetrics} />
          ) : metricsData?.metricsData && metricsData.metricsData.length > 0 ? (
            <div className="mb-6 bg-white rounded-lg shadow-sm p-6 border border text-center text-muted-foreground text-body">
              No duration data for this period
            </div>
          ) : null}

          {/* Cost Metrics */}
          {metricsData?.costMetrics && metricsData.costMetrics.length > 0 ? (
            <CostMetricsTable costMetrics={metricsData.costMetrics} />
          ) : metricsData?.metricsData && metricsData.metricsData.length > 0 ? (
            <div className="mb-6 bg-white rounded-lg shadow-sm p-6 border border text-center text-muted-foreground text-body">
              No cost data for this period
            </div>
          ) : null}

          {/* Campaign Metrics */}
          {metricsData?.campaignMetrics && metricsData.campaignMetrics.length > 0 && (
            <CampaignMetricsTable campaignMetrics={metricsData.campaignMetrics} />
          )}
        </div>
      )}
    </div>
  )
}